import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { cn } from '@/lib/utils';
import { TagOverflowTooltip } from '@/components/recipes/TagOverflowTooltip';
import { Tag } from '@/components/ui/tag';

const readAllergenToken = (entry: unknown): string => {
  if (typeof entry === 'string') return entry.trim();
  if (entry && typeof entry === 'object') {
    const record = entry as Record<string, unknown>;
    const raw = record.value ?? record.id ?? record.name ?? record.label;
    return raw != null ? String(raw).trim() : '';
  }
  return '';
};

export const normalizeRecipeAllergenLabels = (value: unknown): string[] => {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set<string>();
  const labels: string[] = [];

  entries.forEach((entry) => {
    const raw = readAllergenToken(entry);
    if (!raw) return;
    const normalized = raw.toLowerCase().replace(/[-\s]+/g, '_');
    const key = normalized;
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(getDietaryDisplayName(normalized, 'allergies'));
  });

  return labels;
};

interface RecipeAllergenCardProps {
  allergens: unknown;
  className?: string;
  chipClassName?: string;
  title?: string;
  maxVisible?: number;
  compact?: boolean;
}

export const RecipeAllergenCard: React.FC<RecipeAllergenCardProps> = ({
  allergens,
  className,
  chipClassName,
  title = 'Allergens',
  maxVisible = 4,
  compact = false,
}) => {
  const labels = normalizeRecipeAllergenLabels(allergens);
  if (!labels.length) return null;

  const visibleLabels = labels.slice(0, maxVisible);
  const hiddenLabels = labels.slice(maxVisible);
  const overflowClasses = cn(
    'border-red-200 bg-white/80 text-red-700',
    chipClassName,
  );

  if (compact) {
    const compactTitle =
      title.toLowerCase() === 'allergens' ? 'Contains allergens' : title;
    const visibleText = visibleLabels.join(', ');

    return (
      <div
        className={cn('flex min-w-0 flex-wrap items-baseline gap-x-1 text-xs leading-snug text-red-700', className)}
        aria-label={`${compactTitle}: ${labels.join(', ')}`}
      >
        <span className="shrink-0 font-semibold">
          {compactTitle}:
        </span>
        <span className={cn('min-w-0 font-medium leading-snug', chipClassName)} title={labels.join(', ')}>
          {visibleText}{hiddenLabels.length ? ',' : ''}
        </span>
        <TagOverflowTooltip
          label={compactTitle.toLowerCase()}
          values={hiddenLabels.length ? labels : []}
          triggerLabel={`+${hiddenLabels.length} more`}
          className={cn('min-h-0 shrink-0 border-0 bg-transparent px-0 py-0 text-xs font-medium leading-snug text-red-700', chipClassName)}
          itemClassName={cn('border-0 bg-transparent px-0 py-0 text-red-700', chipClassName)}
          showHeading={false}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-red-100 bg-red-50/80 text-red-800',
        'px-2.5 py-2',
        className,
      )}
    >
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase leading-none tracking-normal text-red-700">
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span>{title}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {visibleLabels.map((label) => (
          <Tag
            key={label}
            variant="danger"
            size="xs"
            maxWidth="100%"
            className={cn(
              'rounded-md bg-white/80 px-1.5 py-0.5 font-medium leading-tight text-red-700',
              chipClassName,
            )}
          >
            {label}
          </Tag>
        ))}
        <TagOverflowTooltip
          label={title.toLowerCase()}
          values={hiddenLabels}
          className={overflowClasses}
          itemClassName={overflowClasses}
        />
      </div>
    </div>
  );
};
